<template>
  <div>
    <div class="pb-1 font-medium text-lg">{{ title }}</div>
    <div class="h-96 cursor-default" ref="crossVisitationChart">
      <pre>{{ data }}</pre>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { isEmpty } from "lodash";
import { min, max, sum } from "simple-statistics";
import { select } from "d3-selection";
import { linkHorizontal } from "d3-shape";
import { easeLinear } from "d3-ease";
import "d3-transition";
import { mdiMapMarker } from "@mdi/js";

const props = defineProps({
  title: String,
  data: {
    type: Object,
    default: () => {}
  }
});

const crossVisitationChart = ref(null);

const LABEL_HEIGHT = 16;
const COUNT_SPACE = 58;
const COLOR = "#4E79A7";

async function drawCrossVisitationChart() {
  if (!isEmpty(props.data)) {
    const darkMode = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const width = crossVisitationChart.value.clientWidth;
    const height = crossVisitationChart.value.clientHeight;
    const padding = { top: 20, right: 20, bottom: 20, left: 20, node: 24 };

    // create container
    select(crossVisitationChart.value).selectChildren("*").remove();
    const svg = select(crossVisitationChart.value).append("svg")
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", [0, 0, width, height])
      .style("text-rendering", "optimizeLegibility")
      .style("-webkit-font-smoothing", "antialiased")
      .style("-moz-osx-font-smoothing", "grayscale");

    const maxNumberOfNodes = max([props.data.prior.length, props.data.post.length]);
    const nodeRadius = min([((height - sum([padding.top, padding.bottom, (maxNumberOfNodes - 1) * padding.node, maxNumberOfNodes * LABEL_HEIGHT])) / maxNumberOfNodes) / 2, 26]);
    const priorPoiNodes = svg.append("g").selectAll().data(Object.entries(props.data.prior)).enter().append("g");
    const postPoiNodes = svg.append("g").selectAll().data(Object.entries(props.data.post)).enter().append("g");

    // draw prior POI nodes
    priorPoiNodes.append("path")
      .attr("d", mdiMapMarker)
      .attr("transform", (_, i) => `translate(${padding.left + nodeRadius}, ${padding.top + nodeRadius + i * (nodeRadius * 2 + padding.node + LABEL_HEIGHT)}) scale(${nodeRadius * 2 * 0.85 / 24}) translate(-12,-12)`)
      .attr("fill", COLOR);

    // add prior POI labels
    priorPoiNodes.append("text")
      .text((d) => d[1].poiName)
      .attr("transform", (_, i) => `translate(${padding.left}, ${padding.top + nodeRadius + i * (nodeRadius * 2 + padding.node + LABEL_HEIGHT) + nodeRadius + LABEL_HEIGHT})`)
      .style("text-anchor", "start")
      .style("fill", darkMode ? "#E5E7EB" : "#3F3F46")
      .style("font-size", 12);

    // add prior counts
    priorPoiNodes.append("text")
      .text((d) => (parseFloat(d[1].percentage) * 100).toFixed(2) + "%")
      .attr("transform", (_, i) => `translate(${padding.left + nodeRadius * 2 + 5}, ${padding.top + nodeRadius + 5 + i * (nodeRadius * 2 + padding.node + LABEL_HEIGHT)})`)
      .style("text-anchor", "start")
      .style("fill", darkMode ? "#E5E7EB" : "#3F3F46")
      .style("font-weight", "600")
      .style("font-size", 14);

    // draw post POI nodes
    postPoiNodes.append("path")
      .attr("d", mdiMapMarker)
      .attr("transform", (_, i) => `translate(${width - padding.right - nodeRadius}, ${padding.top + nodeRadius + i * (nodeRadius * 2 + padding.node + LABEL_HEIGHT)}) scale(${nodeRadius * 2 * 0.85 / 24}) translate(-12,-12)`)
      .attr("fill", COLOR);

    // add post POI labels
    postPoiNodes.append("text")
      .text((d) => d[1].poiName)
      .attr("transform", (_, i) => `translate(${width - padding.right}, ${padding.top + nodeRadius + i * (nodeRadius * 2 + padding.node + LABEL_HEIGHT) + nodeRadius + LABEL_HEIGHT})`)
      .style("text-anchor", "end")
      .style("fill", darkMode ? "#E5E7EB" : "#3F3F46")
      .style("font-size", 12);

    // add post counts
    postPoiNodes.append("text")
      .text((d) => (parseFloat(d[1].percentage) * 100).toFixed(2) + "%")
      .attr("transform", (_, i) => `translate(${width - padding.right - nodeRadius * 2 - 5}, ${padding.top + nodeRadius + 5 + i * (nodeRadius * 2 + padding.node + LABEL_HEIGHT)})`)
      .style("text-anchor", "end")
      .style("fill", darkMode ? "#E5E7EB" : "#3F3F46")
      .style("font-weight", "600")
      .style("font-size", 14);

    // draw links between prior nodes and POI node
    const priorLink = linkHorizontal()
      .source(() => [width / 2, height / 2])
      .target((_, i) => [padding.left + nodeRadius * 2 + COUNT_SPACE, padding.top + nodeRadius + i * (nodeRadius * 2 + padding.node + LABEL_HEIGHT)]);

    const priorMarkers = svg.append("g").selectAll(".prior-link").data(props.data.prior).enter()
      .append("path")
      .attr("class", "prior-link")
      .attr("d", priorLink)
      .style("stroke", COLOR)
      .style("fill", "none")
      .style("stroke-width", 3);

    // draw links between post nodes and POI node
    const postLink = linkHorizontal()
      .source(() => [width / 2, height / 2])
      .target((_, i) => [width - padding.right - nodeRadius * 2 - COUNT_SPACE, padding.top + nodeRadius + i * (nodeRadius * 2 + padding.node + LABEL_HEIGHT)]);

    const postMarkers = svg.append("g").selectAll(".post-link").data(props.data.post).enter()
      .append("path")
      .attr("class", "post-link")
      .attr("d", postLink)
      .style("stroke", COLOR)
      .style("fill", "none")
      .style("stroke-width", 3);

    // Animate moving arrows for prior links
    svg.append("g").selectAll(".prior-animated-arrow").data(props.data.prior).enter()
      .append("path")
      .attr("class", "prior-animated-arrow")
      .attr("d", "M 0,0 m -5,-5 L 5,0 L -5,5 Z") // Arrow shape
      .style("fill", COLOR)
      .style("stroke", "none")
      .each(function(d, i) {
        const pathElement = priorMarkers.nodes()[i];
        const totalLength = pathElement.getTotalLength();
        const animateArrow = () => {
          select(this)
            .transition()
            .duration(5000)
            .ease(easeLinear)
            .tween("arrowTween", function() {
              return function(t) {
                const length = (1 - t) * totalLength;
                const point = pathElement.getPointAtLength(length);
                const prevLength = Math.min(totalLength, length + 1);
                const prevPoint = pathElement.getPointAtLength(prevLength);
                const angle = Math.atan2(point.y - prevPoint.y, point.x - prevPoint.x) * (180 / Math.PI);
                select(this).attr("transform", `translate(${point.x}, ${point.y}) rotate(${angle})`);
              };
            })
            .on("end", animateArrow); // Loop the animation
        };
        animateArrow();
      });

    // Animate moving arrows for post links
    svg.append("g").selectAll(".post-animated-arrow").data(props.data.post).enter()
      .append("path")
      .attr("class", "post-animated-arrow")
      .attr("d", "M 0,0 m -5,-5 L 5,0 L -5,5 Z") // Arrow shape
      .style("fill", COLOR)
      .style("stroke", "none")
      .each(function(d, i) {
        const pathElement = postMarkers.nodes()[i];
        const totalLength = pathElement.getTotalLength();
        const animateArrow = () => {
          select(this)
            .transition()
            .duration(5000)
            .ease(easeLinear)
            .tween("arrowTween", function() {
              return function(t) {
                const length = t * totalLength;
                const point = pathElement.getPointAtLength(length);
                const prevLength = Math.max(0, length - 1);
                const prevPoint = pathElement.getPointAtLength(prevLength);
                const angle = Math.atan2(point.y - prevPoint.y, point.x - prevPoint.x) * (180 / Math.PI);
                select(this).attr("transform", `translate(${point.x}, ${point.y}) rotate(${angle})`);
              };
            })
            .on("end", animateArrow); // Loop the animation
        };
        animateArrow();
      });

      // draw POI node
    const poiNode = svg.append("g").selectAll().data(Object.entries(props.data.poi)).enter().append("g");
    poiNode.append("circle")
      .attr("cx", width / 2)
      .attr("cy", height / 2)
      .attr("r", height / 6)
      .style("fill", COLOR)
      .style("stroke", COLOR);

    // add POI label
    poiNode.append("text")
      .text(props.data.poi)
      .attr("x", Math.round(width / 2))
      .attr("y", Math.round(height / 2 + height / 6 + 20) + 0.5)
      .attr("text-anchor", "middle")
      .style("fill", darkMode ? "#E5E7EB" : "#3F3F46")
      .style("font-size", 18);

    poiNode.append("path")
      .attr("d", mdiMapMarker)
      .attr("transform", `translate(${width / 2}, ${height / 2}) scale(${height / 6 * 1.2 / 24}) translate(-12,-12)`)
      .attr("fill", "#FFFFFF");
  }
}

onMounted(drawCrossVisitationChart);

watch(() => props.data, drawCrossVisitationChart, { immediate: true, deep: true });
</script>